import React, { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Spinner, Card, Badge, Button } from 'flowbite-react';
import { FiPlus } from 'react-icons/fi';
import { useDatabase } from '../../hooks/useDatabase';
import { useResource } from '../../hooks/useResource';
import { useResourceReservations } from '../../hooks/useResourceReservations';
import EditableTitle from '../../components/EditableTitle';
import ResourceReservationForm from '../../components/forms/ResourceReservationForm';

const ResourceDetailPage: React.FC<{ overrideResourceId?: string }> = ({ overrideResourceId }) => {
  const { resourceId: paramId } = useParams<{ resourceId: string }>();
  const resourceId = overrideResourceId || paramId;
  const { resource, loading, error } = useResource(resourceId);
  const { reservations } = useResourceReservations(resourceId);
  const { applyCommand, database } = useDatabase();
  const [showReservationForm, setShowReservationForm] = useState(false);

  const teams = useMemo(() => {
    if (!database) return [];
    return Object.entries(database.teams()).map(([id, t]: [string, any]) => ({ id, name: t.name() }));
  }, [database]);

  const updateName = async (newName: string) => {
    if (!resourceId) return;
    await applyCommand({
      type: 'UpdateResource',
      id: resourceId,
      actions: [{ type: 'SetName', name: newName }],
    });
  };

  const updateDescription = async (newDescription: string) => {
    if (!resourceId) return;
    await applyCommand({
      type: 'UpdateResource',
      id: resourceId,
      actions: [{ type: 'SetDescription', description: newDescription || null }],
    });
  };

  const updateTeam = async (teamId: string) => {
    if (!resourceId) return;
    await applyCommand({
      type: 'UpdateResource',
      id: resourceId,
      actions: [{ type: 'SetManagedByTeam', team_id: teamId || null }],
    });
  };

  const updateConcurrency = async (raw: string) => {
    if (!resourceId) return;
    const n = parseInt(raw, 10);
    const limit = Number.isFinite(n) && n > 0 ? n : null;
    await applyCommand({
      type: 'UpdateResource',
      id: resourceId,
      actions: [{ type: 'SetConcurrencyLimit', limit }],
    });
  };

  const updateCooldown = async (raw: string) => {
    if (!resourceId) return;
    const n = parseInt(raw, 10);
    const days = Number.isFinite(n) && n > 0 ? n : null;
    await applyCommand({
      type: 'UpdateResource',
      id: resourceId,
      actions: [{ type: 'SetCooldownDays', days }],
    });
  };

  const deleteReservation = async (reservationId: string) => {
    await applyCommand({
      type: 'DeleteResourceReservation',
      reservation_id: reservationId,
    });
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Spinner size="xl" />
      </div>
    );
  }

  if (error || !resource) {
    return (
      <div className="text-center py-12 text-cm-text-muted">
        {error || 'Resource not found'}
      </div>
    );
  }

  const formatEpoch = (when: any): string => {
    if (!when) return '';
    if (when.type === 'Day') return when.day;
    if (when.type === 'Days') {
      return when.start_inclusive === when.end_inclusive
        ? when.start_inclusive
        : `${when.start_inclusive} → ${when.end_inclusive}`;
    }
    return JSON.stringify(when);
  };

  const describeObject = (object: any): string => {
    if (!object) return 'Blocked';
    if (object.type === 'TaskId') return `Task ${object.id}`;
    if (object.type === 'Tag') return `Tag "${object.tag}"`;
    if (object.type === 'ProjectId') return `Project ${object.id}`;
    return JSON.stringify(object);
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div className="flex items-center gap-3">
        <EditableTitle title={resource.name} onTitleChange={updateName} />
        {resource.concurrencyLimit !== undefined && (
          <Badge color="info">concurrency {resource.concurrencyLimit}</Badge>
        )}
      </div>

      <Card>
        <h3 className="font-mono font-medium text-cm-text-primary mb-3">Details</h3>
        <div className="space-y-4">
          <div>
            <label className="text-sm text-cm-text-muted block mb-1">Description</label>
            <textarea
              className="w-full bg-cm-bg-elevated border border-cm-border-subtle rounded-lg p-3 text-cm-text-primary text-sm resize-y min-h-[80px]"
              value={resource.description || ''}
              onChange={(e) => updateDescription(e.target.value)}
              placeholder="What is this resource used for?"
            />
          </div>

          <div>
            <label className="text-sm text-cm-text-muted block mb-1">Managed by team</label>
            <select
              className="w-full bg-cm-bg-elevated border border-cm-border-subtle rounded-lg p-2 text-cm-text-primary text-sm"
              value={resource.managedByTeamId || ''}
              onChange={(e) => updateTeam(e.target.value)}
            >
              <option value="">— none —</option>
              {teams.map((t) => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm text-cm-text-muted block mb-1">
                Concurrency limit <span className="text-xs">(0 = unlimited)</span>
              </label>
              <input
                type="number"
                min={0}
                className="w-full bg-cm-bg-elevated border border-cm-border-subtle rounded-lg p-2 text-cm-text-primary text-sm"
                value={resource.concurrencyLimit ?? 0}
                onChange={(e) => updateConcurrency(e.target.value)}
              />
            </div>
            <div>
              <label className="text-sm text-cm-text-muted block mb-1">
                Cooldown (days) <span className="text-xs">(0 = none)</span>
              </label>
              <input
                type="number"
                min={0}
                className="w-full bg-cm-bg-elevated border border-cm-border-subtle rounded-lg p-2 text-cm-text-primary text-sm"
                value={resource.cooldownDays ?? 0}
                onChange={(e) => updateCooldown(e.target.value)}
              />
            </div>
          </div>
        </div>
      </Card>

      <Card>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-mono font-medium text-cm-text-primary">Reservations</h3>
          <Button size="xs" onClick={() => setShowReservationForm(true)}>
            <FiPlus className="mr-1" /> New reservation
          </Button>
        </div>

        {reservations.length === 0 ? (
          <p className="text-cm-text-muted text-sm font-mono py-4 text-center">
            No reservations yet.
          </p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-cm-border-subtle">
                <th className="text-left py-2 px-3 font-mono text-cm-text-secondary">When</th>
                <th className="text-left py-2 px-3 font-mono text-cm-text-secondary">For</th>
                <th className="text-left py-2 px-3 font-mono text-cm-text-secondary">Remark</th>
                <th className="w-16" />
              </tr>
            </thead>
            <tbody>
              {reservations.map((r) => (
                <tr key={r.id} className="border-b border-cm-border-subtle">
                  <td className="py-2 px-3 font-mono text-cm-text-primary">{formatEpoch(r.when)}</td>
                  <td className="py-2 px-3">
                    {r.isBlock ? (
                      <Badge color="gray">blocked</Badge>
                    ) : (
                      <span className="text-cm-text-primary">{describeObject(r.object)}</span>
                    )}
                  </td>
                  <td className="py-2 px-3 text-cm-text-secondary text-xs">{r.remark || '—'}</td>
                  <td className="py-2 px-3">
                    <button
                      className="text-xs text-cm-accent-red hover:underline"
                      onClick={() => deleteReservation(r.id)}
                    >
                      delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <ResourceReservationForm
        isOpen={showReservationForm}
        onClose={() => setShowReservationForm(false)}
        onSubmit={applyCommand}
        defaultResourceId={resourceId}
      />
    </div>
  );
};

export default ResourceDetailPage;
